(() => {
  'use strict';

  const ROOT = '[data-h-pdp-complete-look]';
  const CARD = '[data-h-look-card]';
  const SELECT = '[data-h-look-select]';
  const SIZE = '[data-h-look-size]';
  const VARIANTS = '[data-h-look-variants]';
  const STATUS = '[data-h-look-status]';
  const PRICE = '[data-h-look-price]';
  const ADD = '[data-h-look-add]';
  const SUMMARY = '[data-h-look-summary]';
  const ERROR = '[data-h-look-error]';

  class HarpahCompleteLook {
    constructor(root) {
      if (!root || root.dataset.hLookReady === 'true') return;

      this.root = root;
      this.root.dataset.hLookReady = 'true';

      this.includeCurrent = root.dataset.includeCurrent === 'true';
      this.cards = Array.from(root.querySelectorAll(CARD));
      this.addButton = root.querySelector(ADD);
      this.summary = root.querySelector(SUMMARY);
      this.error = root.querySelector(ERROR);

      this.mainBuybox = document.querySelector('[data-h-pdp-buybox]');
      this.mainAtc = this.mainBuybox?.querySelector('[data-h-pdp-atc]') || null;
      this.mainVariantInput = this.mainBuybox?.querySelector('[data-h-pdp-variant-input]') || null;

      this.state = new Map();

      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleAdd = this.handleAdd.bind(this);
      this.handleVariantChange = this.handleVariantChange.bind(this);

      this.prepareCards();
      this.bindEvents();
      this.refresh();
    }

    prepareCards() {
      this.cards.forEach((card) => {
        let variants = [];
        try {
          variants = JSON.parse(card.querySelector(VARIANTS)?.textContent || '[]');
        } catch (_) {}

        const sizePosition = Number(card.dataset.sizeOptionPosition || 0);
        const hasSize = sizePosition > 0;

        this.state.set(card, {
          variants,
          sizePosition,
          hasSize,
          selectedSize: null,
          variant: hasSize ? null : variants.find((v) => v.available) || variants[0] || null
        });
      });
    }

    bindEvents() {
      this.root.addEventListener('click', this.handleClick);
      this.root.addEventListener('change', this.handleChange);
      this.addButton?.addEventListener('click', this.handleAdd);
      document.addEventListener('harpah:variant-change', this.handleVariantChange);
    }

    handleClick(event) {
      const sizeButton = event.target.closest(SIZE);
      if (!sizeButton || !this.root.contains(sizeButton) || sizeButton.disabled) return;

      event.preventDefault();

      const card = sizeButton.closest(CARD);
      const state = this.state.get(card);
      if (!card || !state) return;

      card.querySelectorAll(SIZE).forEach((button) => {
        button.setAttribute('aria-pressed', button === sizeButton ? 'true' : 'false');
      });

      state.selectedSize = String(sizeButton.dataset.sizeValue || '');

      const sizeIndex = state.sizePosition - 1;
      state.variant =
        state.variants.find(
          (variant) => variant.available && String(variant.options[sizeIndex]) === state.selectedSize
        ) || null;

      const select = card.querySelector(SELECT);
      if (select) select.checked = Boolean(state.variant);

      const status = card.querySelector(STATUS);
      if (status) {
        status.textContent = state.variant
          ? `Size ${state.selectedSize} selected.`
          : `Size ${state.selectedSize} is unavailable.`;
      }

      const price = card.querySelector(PRICE);
      if (price && state.variant?.price_formatted) {
        price.textContent = state.variant.price_formatted;
      }

      this.refresh();
    }

    handleChange(event) {
      if (!event.target.matches(SELECT)) return;

      const card = event.target.closest(CARD);
      const state = this.state.get(card);
      if (!card || !state) return;

      if (event.target.checked && state.hasSize && !state.variant) {
        event.target.checked = false;

        const status = card.querySelector(STATUS);
        if (status) status.textContent = 'Select a size first.';

        card.querySelector(`${SIZE}:not(:disabled)`)?.focus();
      }

      this.refresh();
    }

    handleVariantChange(event) {
      // Main PDP variant changed. The current variant input already reflects it.
      this.refresh();
    }

    getSelectedLookItems() {
      const items = [];

      this.cards.forEach((card) => {
        const select = card.querySelector(SELECT);
        const state = this.state.get(card);

        if (!select?.checked || !state?.variant?.available) return;

        items.push({
          id: Number(state.variant.id),
          quantity: 1
        });
      });

      return items;
    }

    mainProductReady() {
      if (!this.includeCurrent) return true;
      if (!this.mainAtc || !this.mainVariantInput) return false;
      return !this.mainAtc.disabled && Boolean(this.mainVariantInput.value);
    }

    refresh() {
      const selected = this.getSelectedLookItems();
      const mainReady = this.mainProductReady();

      if (this.summary) {
        const pieceCount = selected.length + (this.includeCurrent && mainReady ? 1 : 0);

        if (this.includeCurrent && !mainReady) {
          this.summary.innerHTML = '<strong>Select your main product size first</strong><br>Then build the rest of your look here.';
        } else if (selected.length === 0) {
          this.summary.innerHTML = '<strong>Build your set</strong><br>Select at least one matching piece.';
        } else {
          this.summary.innerHTML = `<strong>${pieceCount} piece${pieceCount === 1 ? '' : 's'} selected</strong><br>Ready to add together.`;
        }
      }

      if (this.addButton) {
        this.addButton.disabled = selected.length === 0 || !mainReady;
        this.addButton.textContent =
          this.includeCurrent && !mainReady ? 'Select Main Size First' : 'Add Selected to Bag';
      }
    }

    async handleAdd(event) {
      event.preventDefault();

      if (!this.mainProductReady()) {
        const sizeField = this.mainBuybox?.querySelector('[data-h-pdp-option][data-option-name*="Size" i]');
        sizeField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        sizeField?.querySelector('[data-h-pdp-option-value]:not(:disabled)')?.focus();
        return;
      }

      const lookItems = this.getSelectedLookItems();
      if (!lookItems.length) return;

      const items = [];

      if (this.includeCurrent && this.mainVariantInput?.value) {
        items.push({
          id: Number(this.mainVariantInput.value),
          quantity: 1
        });
      }

      items.push(...lookItems);

      this.setError('');
      this.addButton.disabled = true;
      const original = this.addButton.textContent;
      this.addButton.textContent = 'Adding…';

      const root = window.Shopify?.routes?.root || '/';

      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ items })
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data?.description || data?.message || 'Unable to add the selected look.');
        }

        const data = await response.json();

        this.root.dispatchEvent(
          new CustomEvent('harpah:complete-look-added', {
            bubbles: true,
            detail: { items: data?.items || items }
          })
        );

        this.addButton.textContent = 'Added to Bag';

        document.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true
          })
        );

        window.setTimeout(() => {
          this.addButton.textContent = original;
          this.refresh();
        }, 1600);
      } catch (error) {
        this.setError(error?.message || 'Something went wrong. Please try again.');
        this.addButton.textContent = original;
        this.refresh();
      }
    }

    setError(message) {
      if (!this.error) return;
      this.error.hidden = !message;
      this.error.textContent = message || '';
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll(ROOT).forEach((root) => new HarpahCompleteLook(root));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
